import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { screenshotsApi } from '../api/client';
import { connectEventsSocket } from '../lib/socket';

interface ScreenshotQueryParams {
  userId?: string;
  deviceId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export function useScreenshots(
  params: ScreenshotQueryParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ['screenshots', params],
    queryFn: async () => {
      const response = await screenshotsApi.list(params);
      return response.data;
    },
    enabled: options.enabled,
  });
}

export function useScreenshot(id: string) {
  return useQuery({
    queryKey: ['screenshot', id],
    queryFn: async () => {
      const response = await screenshotsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export type LiveScreenshotStatus =
  | 'idle'
  | 'requesting'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'timed_out'
  | 'device_offline';

export interface LiveScreenshotState {
  status: LiveScreenshotStatus;
  requestId: string | null;
  elapsedMs: number;
  thumbnailUrl: string | null;
  errorMessage: string | null;
}

const IDLE_STATE: LiveScreenshotState = {
  status: 'idle',
  requestId: null,
  elapsedMs: 0,
  thumbnailUrl: null,
  errorMessage: null,
};

// Generous enough for a real capture-upload-confirm round trip over a slow
// connection, but still short enough that "live" stays a meaningful word —
// slightly longer than the backend's own 15s request timeout so the
// server-side timeout (visible via the poll fallback) resolves first in
// the common slow-but-eventually-responds case, rather than the UI giving
// up a moment before the server would have.
const CLIENT_TIMEOUT_MS = 18_000;
// Bounded, and only while a request is actually in flight — this is not a
// general polling loop, just a safety net for the rare case a socket
// message gets dropped (backgrounded tab, transient disconnect).
const POLL_INTERVAL_MS = 3_000;

/**
 * Drives the Screenshots detail page's camera button: kicks off a live
 * capture request, listens for the realtime completion/failure push (see
 * screenshotRequest:update in events.gateway.ts), and falls back to a
 * few bounded status polls if that event is ever missed. See
 * ScreenshotRequestStatus (backend) for what each status actually means;
 * device_offline and failed are terminal states reached immediately or
 * quickly, timed_out is reached client-side after CLIENT_TIMEOUT_MS with
 * no resolution.
 */
export function useLiveScreenshotRequest(deviceId: string) {
  const [state, setState] = useState<LiveScreenshotState>(IDLE_STATE);
  const queryClient = useQueryClient();

  const requestIdRef = useRef<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    pollTimerRef.current = null;
    timeoutTimerRef.current = null;
    tickTimerRef.current = null;
  }, []);

  const resolve = useCallback(
    (next: Partial<LiveScreenshotState>) => {
      clearTimers();
      requestIdRef.current = null;
      setState((prev) => ({ ...prev, ...next, elapsedMs: Date.now() - startedAtRef.current }));
      if (next.status === 'completed') {
        // The new screenshot should also show up in the normal grid/list,
        // not just the transient camera-button UI.
        queryClient.invalidateQueries({ queryKey: ['screenshots'] });
      }
    },
    [clearTimers, queryClient]
  );

  const poll = useCallback(async () => {
    const requestId = requestIdRef.current;
    if (!requestId) return;
    try {
      const response = await screenshotsApi.getRequestStatus(requestId);
      const data = response.data;
      if (data.status === 'completed') {
        resolve({ status: 'completed', thumbnailUrl: data.thumbnailUrl ?? null });
      } else if (data.status === 'failed') {
        resolve({ status: 'failed', errorMessage: data.errorMessage ?? 'Capture failed' });
      } else if (data.status === 'timed_out' || data.status === 'device_offline') {
        resolve({ status: data.status });
      }
      // pending/sent: keep waiting, the next poll or the socket event will catch it.
    } catch {
      // A transient poll failure isn't fatal — the socket event or the
      // next poll tick can still resolve this; only the overall
      // client-side timeout gives up for good.
    }
  }, [resolve]);

  const trigger = useCallback(async () => {
    if (state.status === 'requesting' || state.status === 'waiting') {
      return; // already in flight — the button should be disabled anyway, this is defense in depth
    }

    clearTimers();
    startedAtRef.current = Date.now();
    setState({ ...IDLE_STATE, status: 'requesting' });

    try {
      const response = await screenshotsApi.requestLive(deviceId);
      const { requestId, status } = response.data;

      if (status === 'device_offline') {
        resolve({ status: 'device_offline', requestId });
        return;
      }

      // Already resolved terminal states (a fast completion, or reusing an
      // existing already-finished request) can come back immediately too.
      if (status === 'completed' || status === 'failed' || status === 'timed_out') {
        resolve({ status, requestId });
        return;
      }

      requestIdRef.current = requestId;
      setState({ ...IDLE_STATE, status: 'waiting', requestId });

      tickTimerRef.current = setInterval(() => {
        setState((prev) =>
          prev.status === 'waiting' ? { ...prev, elapsedMs: Date.now() - startedAtRef.current } : prev
        );
      }, 200);

      pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);

      timeoutTimerRef.current = setTimeout(() => {
        resolve({ status: 'timed_out' });
      }, CLIENT_TIMEOUT_MS);
    } catch (err: any) {
      resolve({
        status: 'failed',
        errorMessage: err?.response?.data?.message || 'Could not start the capture request',
      });
    }
  }, [deviceId, state.status, clearTimers, poll, resolve]);

  useEffect(() => {
    const socket = connectEventsSocket();

    const handleUpdate = (message: { data?: { requestId: string; deviceId: string; status: string; thumbnailUrl?: string; errorMessage?: string } }) => {
      const data = message?.data;
      if (!data || data.requestId !== requestIdRef.current || data.deviceId !== deviceId) return;

      if (data.status === 'completed') {
        resolve({ status: 'completed', thumbnailUrl: data.thumbnailUrl ?? null });
      } else if (data.status === 'failed') {
        resolve({ status: 'failed', errorMessage: data.errorMessage ?? 'Capture failed' });
      }
    };

    socket.on('screenshotRequest:update', handleUpdate);
    return () => {
      socket.off('screenshotRequest:update', handleUpdate);
    };
  }, [deviceId, resolve]);

  // Reset to idle whenever the viewed device changes, and stop any timers
  // for the device we're navigating away from.
  useEffect(() => {
    return () => {
      clearTimers();
      requestIdRef.current = null;
    };
  }, [deviceId, clearTimers]);

  const reset = useCallback(() => {
    clearTimers();
    requestIdRef.current = null;
    setState(IDLE_STATE);
  }, [clearTimers]);

  return { state, trigger, reset };
}

export function useDeleteScreenshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => screenshotsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['screenshots'] });
    },
  });
}
